import { Badge, Button, Card, CardContent, Input } from "@exhousing/design-system";
import { ArrowLeft, BookOpen, Undo2, Wallet } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import {
  useCreditInvoice,
  useInvoice,
  useIssueInvoice,
  useRecordPayment,
} from "@/hooks/api/useInvoices";
import { money, vatPct } from "@/lib/money";

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: inv, isLoading } = useInvoice(id);
  const issue = useIssueInvoice();
  const recordPayment = useRecordPayment(id ?? "");
  const credit = useCreditInvoice(id ?? "");
  const [payAmount, setPayAmount] = useState("");

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (!inv) return <p className="text-muted-foreground">Invoice not found.</p>;

  const isOverdue =
    inv.status === "issued" &&
    inv.due_date != null &&
    new Date(inv.due_date) < new Date(new Date().toDateString());

  const onPay = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(payAmount || inv.amount_due);
    if (!amount || amount <= 0) {
      toast.error("Enter a payment amount");
      return;
    }
    recordPayment.mutate(
      { amount },
      {
        onSuccess: () => {
          toast.success("Payment recorded and posted to the ledger");
          setPayAmount("");
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  // The double-entry the ledger holds for this invoice (Dr AR / Cr revenue + VAT).
  const postings = [
    { account: "Accounts receivable (AR)", debit: inv.total, credit: 0 },
    { account: "Rental revenue", debit: 0, credit: inv.subtotal },
    ...(inv.vat_total > 0
      ? [{ account: "VAT payable", debit: 0, credit: inv.vat_total }]
      : []),
  ];

  const onIssue = () =>
    issue.mutate(inv.id, {
      onSuccess: () => toast.success("Invoice issued and posted to the ledger"),
      onError: (e) => toast.error(e.message),
    });

  const canCredit = inv.status === "issued" || inv.status === "paid";
  const onCredit = () => {
    if (
      !window.confirm(
        "Credit (fully reverse) this invoice? It posts a credit note and can't be undone.",
      )
    )
      return;
    const reason = window.prompt("Reason for the credit note (optional):");
    credit.mutate(reason, {
      onSuccess: (n) => toast.success(`Credit note ${n.number} created`),
      onError: (e) => toast.error(e.message),
    });
  };

  return (
    <div className="space-y-6">
      <Link
        to="/invoices"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Invoices
      </Link>

      <header className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl font-medium tracking-tight">
              {inv.number ?? "Draft invoice"}
            </h1>
            <StatusBadge status={inv.status} />
            {isOverdue && (
              <Badge tone="danger" dot>
                Overdue
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {inv.issue_date ? `Issued ${inv.issue_date}` : "Not yet issued"}
            {inv.due_date ? ` · due ${inv.due_date}` : ""}
          </p>
        </div>
        {inv.status === "draft" && (
          <Button onClick={onIssue} disabled={issue.isPending}>
            {issue.isPending ? "Issuing…" : "Issue invoice"}
          </Button>
        )}
        {canCredit && (
          <Button variant="outline" onClick={onCredit} disabled={credit.isPending}>
            <Undo2 className="size-4" />
            {credit.isPending ? "Crediting…" : "Credit"}
          </Button>
        )}
      </header>

      {/* Lines */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3 font-medium">Description</th>
                <th className="px-5 py-3 text-right font-medium">Qty</th>
                <th className="px-5 py-3 text-right font-medium">Unit</th>
                <th className="px-5 py-3 text-right font-medium">VAT</th>
                <th className="px-5 py-3 text-right font-medium">Net</th>
                <th className="px-5 py-3 text-right font-medium">Gross</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l) => (
                <tr key={l.id} className="border-b last:border-0">
                  <td className="px-5 py-3">{l.description}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{l.quantity}</td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {money(l.unit_amount, inv.currency)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                    {vatPct(l.vat_rate)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {money(l.net, inv.currency)}
                  </td>
                  <td className="px-5 py-3 text-right font-medium tabular-nums">
                    {money(l.gross, inv.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="text-sm">
                <td colSpan={4} />
                <td className="px-5 py-2 text-right text-muted-foreground">Subtotal</td>
                <td className="px-5 py-2 text-right tabular-nums">
                  {money(inv.subtotal, inv.currency)}
                </td>
              </tr>
              <tr className="text-sm">
                <td colSpan={4} />
                <td className="px-5 py-2 text-right text-muted-foreground">VAT</td>
                <td className="px-5 py-2 text-right tabular-nums">
                  {money(inv.vat_total, inv.currency)}
                </td>
              </tr>
              <tr className="border-t font-semibold">
                <td colSpan={4} />
                <td className="px-5 py-3 text-right">Total</td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {money(inv.total, inv.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Ledger impact — the trust differentiator: every figure ties to the books. */}
      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <BookOpen className="size-4 text-accent" />
            How this posts to the ledger
            {inv.journal_entry_id && (
              <span className="text-xs font-normal text-muted-foreground">
                · entry {inv.journal_entry_id.slice(0, 8)}
              </span>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-2 font-medium">Account</th>
                <th className="px-2 py-2 text-right font-medium">Debit</th>
                <th className="px-2 py-2 text-right font-medium">Credit</th>
              </tr>
            </thead>
            <tbody>
              {postings.map((p) => (
                <tr key={p.account} className="border-b last:border-0">
                  <td className="px-2 py-2">{p.account}</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {p.debit ? money(p.debit, inv.currency) : ""}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {p.credit ? money(p.credit, inv.currency) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-medium">
                <td className="px-2 py-2 text-muted-foreground">Balanced</td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {money(inv.total, inv.currency)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {money(inv.total, inv.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
          {inv.status === "draft" && (
            <p className="mt-3 text-xs text-muted-foreground">
              Drafts don't touch the ledger. Issuing posts the entry above.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Payments — record against the invoice; clears AR and marks it paid. */}
      {inv.status !== "draft" && (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center gap-2 text-sm font-medium">
              <Wallet className="size-4 text-accent" />
              Payments
            </div>
            {/* Payment progress — paid vs. total, with the remaining balance. */}
            {(() => {
              const total = inv.total || 0;
              const pct =
                total > 0
                  ? Math.min(100, Math.round((inv.amount_paid / total) * 100))
                  : 0;
              const fillColor = isOverdue
                ? "bg-danger"
                : inv.amount_due <= 0
                  ? "bg-success"
                  : "bg-accent";
              return (
                <div className="mb-4 space-y-2">
                  <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                    <span className="text-muted-foreground">
                      Paid:{" "}
                      <span className="font-semibold tabular-nums text-foreground">
                        {money(inv.amount_paid, inv.currency)}
                      </span>
                      <span className="text-muted-foreground">
                        {" "}
                        of {money(inv.total, inv.currency)}
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      Outstanding:{" "}
                      <span
                        className={`font-semibold tabular-nums ${
                          inv.amount_due > 0 ? "text-foreground" : "text-emerald-600"
                        }`}
                      >
                        {money(inv.amount_due, inv.currency)}
                      </span>
                    </span>
                  </div>
                  <div
                    className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Payment progress"
                  >
                    <div
                      className={`h-full rounded-full transition-all ${fillColor}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {inv.payments.length > 0 && (
              <table className="mb-4 w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-2 py-2 font-medium">Date</th>
                    <th className="px-2 py-2 font-medium">Method</th>
                    <th className="px-2 py-2 font-medium">Status</th>
                    <th className="px-2 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.payments.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="px-2 py-2 text-muted-foreground">
                        {p.charge_date ?? p.created_at.slice(0, 10)}
                      </td>
                      <td className="px-2 py-2 capitalize">{p.method.replace("_", " ")}</td>
                      <td className="px-2 py-2">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {money(p.amount, inv.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {inv.amount_due > 0 ? (
              <form onSubmit={onPay} className="flex items-end gap-3">
                <div className="w-40 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Record payment</label>
                  <Input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder={String(inv.amount_due)}
                    className="text-right tabular-nums"
                  />
                </div>
                <Button type="submit" disabled={recordPayment.isPending}>
                  {recordPayment.isPending ? "Recording…" : "Record payment"}
                </Button>
              </form>
            ) : (
              <p className="text-sm text-emerald-600">Fully paid.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
